// A leitura do que a equipe digita sobre um concurso: a janela de inscrição, a
// situação dela, as vagas e a remuneração.
//
// Puro, sem UM import, com prova própria (`app/tests/concursos.mjs`). O ETL
// carrega este mesmo arquivo transpilando (o caminho de `slug.ts` e de
// `calendarioFiscal.ts`), então existe UMA definição de cada regra.
//
// ===========================================================================
// Por que este módulo existe: na origem a mesma regra estava escrita TRÊS vezes
// ===========================================================================
// No app da Lovable, "quando as inscrições encerram" tem três implementações que
// discordam (medido em 27/08/2026):
//
//   1. `src/lib/parseInscricaoDate.ts` — pega a ÚLTIMA data do texto, às
//      23:59:59, e exige pelo menos DUAS datas;
//   2. `src/components/Header.tsx` — pega a PRIMEIRA ocorrência de "a <data>",
//      à meia-noite, com um regex sem fronteira de palavra (então o "a" de "para"
//      e de "até" casa por acidente);
//   3. `src/components/admin/AdminAlerts.tsx` — pega a SEGUNDA data encontrada.
//
// No texto `"Inscricoes: 01/09/2026 a 30/09/2026 (etapa a 05/10/2026)"` as três
// respondem 30/09, 05/10 e 30/09. É a cicatriz da `000106`/`000109` do hub — a
// segunda cópia da regra diverge em silêncio, porque não há quem compare.
//
// ===========================================================================
// A regra se levanta do DADO, e ela diz o que ACEITA
// ===========================================================================
// As 256 linhas reais foram medidas por FORMA (todo dígito virou 9, toda letra
// virou A) em 27/08/2026, e deram **17 formas distintas** para o mesmo campo:
//
//    141x  "26/05/2026 a 26/06/2026"
//     58x  "10h 02/04/2026 a 04/05/2026 16h"
//     32x  "09h 06/04/2026 a 20/04/2026 "
//      6x  "20/05/2026 a 23/06/2026 17h"
//      4x  "09/03/2026 08h a 24/03/2026"       <- a hora DEPOIS da data
//      3x  "16h 20/5/2026 a 18/6/2026 16h"     <- dia e mês com UM dígito
//      2x  "09h 22/06/26 a 21/07/26 16:00"     <- ano com DOIS dígitos
//      1x  "02/03/ 2026 16h a 01/04/2026 16h"  <- espaço depois da barra
//      1x  "08h30 16/04/2026 a 02/06/2026"
//      1x  "14/08/2026 até 30/08/2026"         <- "até" em vez de "a"
//      1x  "23/04 a 07/05/2026"                <- a primeira data SEM ano
//      1x  "2907/2026 a 30/08/2026"            <- barra faltando (erro de digitação)
//      1x  "ADIADAS"
//      1x  null
//
// A regex do app de origem é `/(\d{2})\/(\d{2})\/(\d{4})/`: ela não vê as 3 de
// um dígito nem as 3 de ano curto. Esta aqui vê — e as duas últimas formas
// ("23/04 a …" e "2907/2026") continuam SEM leitura de propósito: adivinhar o
// ano de uma data de inscrição é errar sem sinal nenhum, e o certo é a tela
// dizer "confira no edital".
//
// A regra está escrita pelo que ACEITA, e não pela lista do que recusa — a
// lição da `000101`, onde uma lista de permitidos em ASCII recusou o LinkedIn de
// 31 alunos chamados João.
//
// ===========================================================================
// Data-só NUNCA passa por `new Date(iso)`
// ===========================================================================
// `new Date("2026-02-04")` é meia-noite **UTC**; formatado em UTC-3 sai
// "03/02/2026". Medido no app de origem: o PDF (`src/lib/exportPdf.ts:35`), o
// card (`ConcursoCard.tsx:101`) e o detalhe (`ConcursoDetailDialog.tsx:72`)
// imprimem a data da prova UM DIA ANTES por causa disso. Relatório que diz ao
// concurseiro que a prova é um dia antes é pior que não ter relatório.
//
// Então aqui coluna `date` se lê por SPLIT de string, e o único `Date` que
// aparece recebe (ano, mês, dia) separados.

/** A situação da inscrição — quatro estados, e o quarto não é enfeite. */
export type Situacao = "aberta" | "a_abrir" | "encerrada" | "a_conferir";

export type Janela = { inicio: string; fim: string };

/** Dia de hoje em ISO (aaaa-mm-dd), pelo relógio LOCAL de quem olha a tela. */
export function hojeIso(agora: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${agora.getFullYear()}-${p(agora.getMonth() + 1)}-${p(agora.getDate())}`;
}

/**
 * As datas que existem num texto livre, em ISO, na ordem em que aparecem.
 *
 * Aceita `dd/mm/aaaa`, `d/m/aaaa`, `dd/mm/aa` e espaço em volta das barras —
 * as formas MEDIDAS. Recusa data que não existe no calendário (31/02) e ano
 * fora de 2020–2099, porque `2907/2026` casaria como 29/07 se a fronteira de
 * palavra não estivesse ali.
 */
export function datasDoTexto(texto: string | null | undefined): string[] {
  if (!texto) return [];
  const achadas: string[] = [];
  // `\b` nas duas pontas: sem ela, "2907/2026" casaria como "07/2026" com o
  // "29" sobrando, e a linha da Prefeitura de Piraju (erro de digitação real)
  // entraria com data inventada em vez de cair em "a conferir".
  const re = /\b(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{2}|\d{4})\b/g;
  for (const m of String(texto).matchAll(re)) {
    const dia = Number(m[1]);
    const mes = Number(m[2]);
    // Ano de dois dígitos é 20xx. Não existe inscrição de concurso de 1926, e
    // as duas linhas medidas ("22/06/26") são de 2026.
    const ano = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    if (dia < 1 || dia > 31 || mes < 1 || mes > 12) continue;
    if (ano < 2020 || ano > 2099) continue;
    // A data EXISTE? 31/02 não existe, e aceitar produziria "2026-02-31", que o
    // Postgres recusa na carga — erro no fim da esteira em vez de aviso na tela.
    const d = new Date(Date.UTC(ano, mes - 1, dia));
    if (d.getUTCDate() !== dia || d.getUTCMonth() !== mes - 1) continue;
    achadas.push(`${ano}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`);
  }
  return achadas;
}

/**
 * A janela de inscrição de um texto livre — ou `null` quando não dá para ler.
 *
 * `null` tem TRÊS causas, e as três terminam na mesma tela ("a conferir no
 * edital"): menos de duas datas (5 linhas), data ilegível (4 linhas) e janela
 * invertida (3 linhas — `"16h 22/06/2026 a 23/04/2026 16h"`, da CRA/PR, onde o
 * fim vem antes do início).
 *
 * A invertida é recusada, e não "consertada" trocando as duas: não se sabe qual
 * das duas datas está errada, e escolher uma é inventar prazo.
 */
export function janelaDeInscricao(texto: string | null | undefined): Janela | null {
  const ds = datasDoTexto(texto);
  if (ds.length < 2) return null;
  const inicio = ds[0];
  const fim = ds[ds.length - 1];
  if (inicio > fim) return null;
  return { inicio, fim };
}

/**
 * A situação, a partir dos FATOS — nunca de um rótulo gravado.
 *
 * Na origem o rótulo É gravado (`status_lista`), e as três respostas para
 * "quantos estão abertos?" divergem (medido em 27/08/2026, 256 linhas):
 *
 *   o que o banco GRAVOU ....... 250
 *   o que a tela MOSTRA ........ 50
 *   o que os FATOS dizem ....... 32
 *
 * A diferença entre a tela e os fatos são 18 concursos anunciados como abertos
 * que não estão. E o rótulo gravado tem SETE grafias para dois significados,
 * incluindo "Incrições Abertas" e "Inhscrições Abertas" — é texto digitado à
 * mão, então nem serve de chave.
 *
 * `a_conferir` não é o mesmo que "aberta". A origem trata assim (o parser
 * devolve null e o front chama de aberta), e é a direção errada: prazo
 * inventado manda o aluno a uma inscrição que fechou. Dizer "confira no edital"
 * é a única resposta honesta — a cicatriz do "Limite com a prorrogação" do HUB
 * DP, onde a ausência de uma linha que carrega aviso é lida como "está tudo bem".
 */
export function situacaoDaInscricao(janela: Janela | null, hoje: string): Situacao {
  if (!janela) return "a_conferir";
  if (hoje < janela.inicio) return "a_abrir";
  if (hoje <= janela.fim) return "aberta";
  return "encerrada";
}

/** Quantos dias faltam de `hoje` até `alvo` — os dois em ISO, sem fuso no meio. */
export function diasAte(hoje: string, alvo: string): number {
  const ms = diaEmMs(alvo) - diaEmMs(hoje);
  return Math.round(ms / 86400000);
}

function diaEmMs(iso: string): number {
  const [a, m, d] = iso.split("-").map(Number);
  return Date.UTC(a, m - 1, d);
}

/**
 * As vagas IMEDIATAS de um texto como "1 + CR", "CR", "21 + CR", "25 CR".
 *
 * "CR" é cadastro de reserva: o candidato passa e entra numa fila, sem vaga
 * agora. Medido: **83 dos 256 concursos são só CR** — um terço do catálogo tem
 * zero vaga imediata. Somar "CR" como vaga inflaria o número mais visível da
 * ferramenta em um terço.
 *
 * Devolve 0 para "CR" puro, e é 0 de propósito e não `null`: zero vaga imediata
 * é um FATO sobre o concurso, e a tela diz "cadastro de reserva" com letra.
 */
export function vagasImediatas(texto: string | null | undefined): number {
  if (!texto) return 0;
  const m = String(texto).match(/^\s*(\d+)/);
  return m ? Number(m[1]) : 0;
}

/**
 * O valor em reais que a remuneração ANUNCIA — o primeiro número do texto.
 *
 * Por que o PRIMEIRO e não o maior: o texto é sempre "o salário, e depois os
 * penduricalhos" — `"R$ 5.215,39 + R$ 1.175,00 (Auxílio Alimentação)"`,
 * `"Inicial: R$ 2.940,52 Vale alimentação: R$ 308,50"`. O maior seria o salário
 * em 22 linhas e o auxílio em nenhuma, mas somar os dois responderia outra
 * pergunta ("quanto entra na conta") que o texto não dá para todos.
 *
 * ---------------------------------------------------------------------------
 * Por que isto é DERIVADO e não um campo digitado
 * ---------------------------------------------------------------------------
 * Na origem são DOIS campos no formulário: `remuneracao` (texto livre) e
 * `remuneracao_min` (número), digitados à mão, sem ninguém conferir um contra o
 * outro. Medido: **7 das 256 linhas divergem**, e os erros são grandes —
 *
 *    "R$ 3.497,52"  gravado como       3,49752   (a vírgula lida como decimal)
 *    "R$ 23.264,47" gravado como     233.264,47  (um dígito a mais)
 *    "R$ 2.624,48"  gravado como      22.624,48
 *
 * — e o número gravado é o que alimenta o filtro de salário mínimo E a
 * ordenação "maior remuneração". Ou seja: o TJ/PR aparece no topo da lista dos
 * melhores salários com R$ 233 mil, e três concursos de R$ 3 a 5 mil afundam
 * como se pagassem três reais. Um fato, dois campos, nenhuma guarda.
 *
 * Aqui o número sai do texto, sempre, num lugar só. O `sanidade` abaixo é a
 * segunda guarda, no banco.
 */
export function remuneracaoBase(texto: string | null | undefined): number | null {
  if (!texto) return null;
  // Formato brasileiro: ponto é milhar, vírgula é decimal. As formas medidas
  // incluem "R$ 3.100,00", "R$3.036,28", "R$ 3160,09" (sem milhar) e
  // "5.248,97" (sem o R$). O regex pega o primeiro grupo de dígitos com
  // separadores e nada mais.
  const m = String(texto).match(/(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{1,2}))?/);
  if (!m) return null;
  const inteiro = m[1].replace(/\./g, "");
  const centavos = m[2] ? m[2].padEnd(2, "0") : "00";
  const v = Number(`${inteiro}.${centavos}`);
  if (!Number.isFinite(v) || v <= 0) return null;
  return v;
}

/**
 * O valor está numa faixa plausível para salário de concurso contábil?
 *
 * Não é validação de entrada — é o SINAL para a tela de admin. Os limites saem
 * da medição das 256 linhas: a mediana é R$ 5.500, o menor plausível R$ 1.950
 * (uma câmara pequena) e o maior R$ 20.639,84. Quatro linhas estão abaixo de
 * R$ 100 e uma acima de R$ 100 mil, e as cinco são erro de digitação.
 *
 * A faixa é generosa de propósito: R$ 500 pega o salário mínimo pela metade e
 * R$ 80.000 pega qualquer teto do funcionalismo. Quem cair fora dela é
 * MARCADO, não recusado — recusar impediria a equipe de cadastrar um concurso
 * de verdade com valor incomum, e a doutrina da casa é que a constraint que não
 * nasce de medição é palpite com força de lei (a `000077`/`000079`).
 */
export function remuneracaoPlausivel(valor: number | null): boolean {
  if (valor === null) return true; // sem valor não é valor errado
  return valor >= 500 && valor <= 80000;
}

/** `2026-09-13` → `13/09/2026`. Por SPLIT, nunca por `new Date`. */
export function dataLonga(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).split("-");
  if (!a || !m || !d) return "—";
  return `${d}/${m}/${a}`;
}

/** `2026-09-13` → `13/09`. */
export function dataCurta(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [, m, d] = String(iso).split("-");
  if (!m || !d) return "—";
  return `${d}/${m}`;
}

const MESES = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

/** `2026-09` → `setembro de 2026`. Para agrupar provas por mês. */
export function mesLongo(anoMes: string): string {
  const [a, m] = anoMes.split("-");
  const i = Number(m) - 1;
  return `${MESES[i] ?? m} de ${a}`;
}

/**
 * O texto do prazo, quando ele é URGENTE — e `null` quando não é.
 *
 * A origem tem faixas de cor por urgência (<=3 vermelho, <=7 laranja, <=15
 * âmbar) e um limite de 15 dias para aparecer no alerta. Mantido o 15, porque é
 * a janela em que dá para reunir documento e pagar taxa; e o texto é o que a
 * tela lê, não a cor — cor sozinha não informa quem não distingue vermelho de
 * laranja.
 */
export function urgenciaDoPrazo(
  dias: number,
): { texto: string; nivel: "hoje" | "critico" | "atencao" | "calmo" } | null {
  if (dias < 0 || dias > 15) return null;
  if (dias === 0) return { texto: "encerra hoje", nivel: "hoje" };
  if (dias === 1) return { texto: "encerra amanhã", nivel: "critico" };
  if (dias <= 3) return { texto: `encerra em ${dias} dias`, nivel: "critico" };
  if (dias <= 7) return { texto: `encerra em ${dias} dias`, nivel: "atencao" };
  return { texto: `encerra em ${dias} dias`, nivel: "calmo" };
}

/**
 * As 27 UFs, para o mapa e para a conferência.
 *
 * Constante, e não parâmetro do banco: a divisão política do Brasil não muda
 * por ato do Executivo nem por lei anual — é o corte da `000108`, o mesmo que
 * põe a geometria do mapa em `mapaBrasil.ts` e os links das juntas no banco.
 */
export const UFS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
] as const;

export function ufValida(uf: string | null | undefined): boolean {
  return !!uf && (UFS as readonly string[]).includes(String(uf).trim().toUpperCase());
}

// ===========================================================================
// O FILTRO DE CARGO NÃO EXISTE AQUI, e a medição é a razão
// ===========================================================================
// O app de origem tem um filtro de cargo com seis opções cravadas no front —
// "Contador", "Assistente", "Analista", "Auditor", "Perito", "Outros". Eu
// comecei a portá-lo como uma função `familiaDoCargo`, e a medição sobre as 256
// linhas reais (27/08/2026) mostrou que ele não filtra nada:
//
//    236  contador          (92% do catálogo)
//     10  técnico
//      6  controle interno
//      4  auditor
//
// São 26 grafias distintas de cargo, e 234 delas contêm a palavra "Contador".
// Filtro em que uma opção pega 92% das linhas não separa — é a mesma medição que
// tirou a etiqueta "com orientação" do Gerenciador de Estudos, onde ela
// apareceria em 179 das 275 metas.
//
// Pior: das seis opções da origem, **"Perito" alcança ZERO linhas** e
// "Assistente" alcança uma. O filtro oferece duas gavetas vazias.
//
// E a primeira versão da minha função tinha um defeito que a prova pegou: eu
// escrevi a lista de radicais de cabeça ("contabil", "contabilidade") e
// "Contábeis" sem acento é "contabeis", que NÃO contém "contabil" — as 4 linhas
// de "Ciências Contábeis" caíam em "outros". É a cicatriz do `[i]` que apagou
// 216 imagens do IR Pro: **lista levantada de memória, não do dado**. O radical
// certo é "contab", e foi a medição por VALOR EXATO que o revelou.
//
// O que sobrou é honesto: o cargo entra na BUSCA livre (por `paraBusca`), e a
// distinção que o concurseiro realmente faz — nível superior contra nível médio
// — não está no dado. Só 4 linhas dizem "Técnico em Contabilidade" com essas
// palavras, e se "Analista Técnico/Contador" é de nível médio ou superior é
// pergunta de CONTEÚDO, não de implementação. Está nomeada no runbook para
// Victor decidir; enquanto não estiver decidida, não se inventa a resposta.

/**
 * A banca, normalizada para AGRUPAR — nunca para exibir.
 *
 * Medido: 128 grafias de banca em 256 linhas, e **14 grupos são a mesma banca
 * escrita de dois jeitos** ("Vunesp"/"VUNESP", "IMESO"/"Imeso",
 * "Fundação Fafipa"/"Fundação FAFIPA", "INEPAM"/"Inepam"…). O filtro da origem
 * é pior ainda: uma lista FECHADA de cinco nomes cravada no front, que casa por
 * igualdade exata e alcança **4% do catálogo** (9 de 256 linhas).
 *
 * Aqui o filtro se levanta do dado e agrupa por esta chave, mas o nome que
 * aparece na tela é o que a equipe digitou — normalizar para exibir apagaria a
 * grafia oficial da banca.
 */
export function chaveDaBanca(banca: string | null | undefined): string {
  return semAcento(String(banca ?? ""))
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// O acento sai por FILTRO DE CODEPOINT, e não por uma faixa de caracteres
// dentro de um colchete. As marcas combinantes (U+0300 a U+036F) são bytes
// INVISÍVEIS no editor: escritas literalmente, ninguém as vê para conferir, e
// este módulo atravessa duas camadas — o Vite para a tela e o esbuild do ETL
// para o Node. É a cicatriz da casa sobre expressão que cruza camadas de
// string: prefira a forma que não tem escape. Mesma técnica de
// `GerenciadorMeta.tsx`.
//
// Os dois números são 768 e 879 em decimal, e não em hexadecimal, pelo mesmo
// motivo: `0x300` escrito aqui atravessa intacto, mas decimal não tem como ser
// confundido com um escape por nenhuma camada.
function semAcento(s: string): string {
  return s
    .normalize("NFD")
    .split("")
    .filter((c) => {
      const p = c.codePointAt(0) ?? 0;
      return p < 768 || p > 879;
    })
    .join("");
}

/**
 * Busca sem acento e sem caixa, para o campo de procurar.
 *
 * O aluno digita "sao paulo" e o dado diz "São Paulo"; digita "camara" e o dado
 * diz "Câmara" (63 linhas). Comparar cru perderia as duas.
 */
export function paraBusca(s: string | null | undefined): string {
  return semAcento(String(s ?? "")).toLowerCase().trim();
}
